// AGC: Yield models configuration — editable base yields, thresholds, and adjustment curves per crop
import React, { useState, useEffect, useCallback } from 'react'
import { TrendingUp, Save, Loader2, AlertCircle } from 'lucide-react'
import { getBackendUrl } from '../../../utils/backendUrl'
import { useTranslation } from '../../../hooks/useTranslation'
import type { CropYieldModel } from '../../../types/admin/models'

const AUTH_HEADERS = { 'Authorization': 'Bearer admin-token', 'Content-Type': 'application/json' }

const CROP_LABELS: Record<string, { name: string; icon: string }> = {
  soja: { name: 'Soja', icon: '🫘' },
  milho: { name: 'Milho', icon: '🌽' },
  milho_safrinha: { name: 'Milho Safrinha', icon: '🌽' },
  trigo: { name: 'Trigo', icon: '🌾' },
  algodao: { name: 'Algodão', icon: '🏵️' },
  feijao: { name: 'Feijão', icon: '🫘' },
  arroz: { name: 'Arroz', icon: '🍚' },
  sorgo: { name: 'Sorgo', icon: '🌾' },
  cafe: { name: 'Café', icon: '☕' },
  cana: { name: 'Cana', icon: '🎋' },
}

export const YieldModelsConfig: React.FC = () => {
  const { t } = useTranslation()
  const [crops, setCrops] = useState<Record<string, CropYieldModel>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [source, setSource] = useState<string>('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/yield-models`, { headers: AUTH_HEADERS })
      const data = await res.json()
      if (data.success) {
        setCrops(data.data.crops || {})
        setSource(data.data.source || 'unknown')
        setDirty(false)
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const updateCrop = (cropId: string, field: keyof CropYieldModel, value: number | string) => {
    setCrops(prev => {
      const existing = prev[cropId]
      if (!existing) return prev
      const updated: CropYieldModel = { ...existing, [field]: value } as CropYieldModel
      return { ...prev, [cropId]: updated }
    })
    setDirty(true)
  }

  const updateThreshold = (cropId: string, level: string, value: number) => {
    setCrops(prev => {
      const existing = prev[cropId]
      if (!existing) return prev
      const updated: CropYieldModel = {
        ...existing,
        scoreThresholds: { ...existing.scoreThresholds, [level]: value },
      }
      return { ...prev, [cropId]: updated }
    })
    setDirty(true)
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`${getBackendUrl()}/api/v1/admin/config/yield-models`, {
        method: 'PUT',
        headers: AUTH_HEADERS,
        body: JSON.stringify({ crops }),
      })
      const data = await res.json()
      if (data.success) {
        setDirty(false)
        setSource('admin-panel')
      } else {
        setError(data.error || 'Save failed')
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-emerald-500" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('admin.yieldModels.title')}</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
            {source === 'embrapa-defaults' ? 'EMBRAPA Defaults' : source === 'admin-panel' ? t('admin.yieldModels.customized') : source}
          </span>
        </div>
        {dirty && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t('admin.save')}
          </button>
        )}
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
              <th className="pb-2 pr-3">{t('admin.yieldModels.crop')}</th>
              <th className="pb-2 px-2 text-center">{t('admin.yieldModels.baseYield')}</th>
              <th className="pb-2 px-2 text-center">{t('admin.yieldModels.minYield')}</th>
              <th className="pb-2 px-2 text-center">{t('admin.yieldModels.maxYield')}</th>
              <th className="pb-2 px-2 text-center">{t('admin.yieldModels.excellent')}</th>
              <th className="pb-2 px-2 text-center">{t('admin.yieldModels.good')}</th>
              <th className="pb-2 px-2 text-center">{t('admin.yieldModels.moderate')}</th>
              <th className="pb-2 px-2 text-center">{t('admin.yieldModels.curve')}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(crops).map(([cropId, model]) => {
              const info = CROP_LABELS[cropId] || { name: cropId, icon: '🌱' }
              return (
                <tr key={cropId} className="border-b border-gray-100 dark:border-gray-800">
                  <td className="py-2 pr-3 font-medium text-gray-900 dark:text-white">
                    <span className="mr-1">{info.icon}</span>{info.name}
                  </td>
                  <td className="py-2 px-1"><input type="number" step="0.1" value={model.baseYieldTnHa} onChange={e => updateCrop(cropId, 'baseYieldTnHa', parseFloat(e.target.value) || 0)} className="w-16 px-1 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs" /></td>
                  <td className="py-2 px-1"><input type="number" step="0.1" value={model.minYieldTnHa} onChange={e => updateCrop(cropId, 'minYieldTnHa', parseFloat(e.target.value) || 0)} className="w-16 px-1 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs" /></td>
                  <td className="py-2 px-1"><input type="number" step="0.1" value={model.maxYieldTnHa} onChange={e => updateCrop(cropId, 'maxYieldTnHa', parseFloat(e.target.value) || 0)} className="w-16 px-1 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs" /></td>
                  <td className="py-2 px-1"><input type="number" value={model.scoreThresholds?.excelente ?? 80} onChange={e => updateThreshold(cropId, 'excelente', parseInt(e.target.value) || 0)} className="w-14 px-1 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs" /></td>
                  <td className="py-2 px-1"><input type="number" value={model.scoreThresholds?.bom ?? 60} onChange={e => updateThreshold(cropId, 'bom', parseInt(e.target.value) || 0)} className="w-14 px-1 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs" /></td>
                  <td className="py-2 px-1"><input type="number" value={model.scoreThresholds?.moderado ?? 40} onChange={e => updateThreshold(cropId, 'moderado', parseInt(e.target.value) || 0)} className="w-14 px-1 py-1 text-center border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs" /></td>
                  <td className="py-2 px-1">
                    <select value={model.yieldAdjustmentCurve || 'linear'} onChange={e => updateCrop(cropId, 'yieldAdjustmentCurve', e.target.value)} className="px-1 py-1 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs">
                      <option value="linear">Linear</option>
                      <option value="exponential">Exp</option>
                    </select>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default YieldModelsConfig
